// src/lib/firebase.ts
// ── Firebase Configuration — BookBuilderGo ────────────────────────
import type { FirebaseApp } from "firebase/app";
import type { Auth, User, UserCredential } from "firebase/auth";

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY            || "AIzaSyDX3jM7ry2rq0JhMa1U6tMvnwfK4cv0-Yo",
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN        || "bookbuildergo.firebaseapp.com",
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID         || "bookbuildergo",
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET     || "bookbuildergo.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID|| "297755716545",
  appId:             import.meta.env.VITE_FIREBASE_APP_ID             || "1:297755716545:web:9764a192246df89d615d40",
  measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID     || "G-LRGSRXW6YN",
};

let appPromise: Promise<FirebaseApp> | null = null;
let authPromise: Promise<Auth> | null = null;

async function getFirebaseApp() {
  if (!appPromise) {
    appPromise = import("firebase/app").then(({ getApp, getApps, initializeApp }) =>
      getApps().length ? getApp() : initializeApp(firebaseConfig),
    );
  }
  return appPromise;
}

async function getFirebaseAuthModule() {
  return import("firebase/auth");
}

export async function getAuthInstance() {
  if (!authPromise) {
    authPromise = Promise.all([getFirebaseApp(), getFirebaseAuthModule()]).then(([app, { getAuth }]) =>
      getAuth(app),
    );
  }
  return authPromise;
}

export async function getCurrentUser() {
  return (await getAuthInstance()).currentUser;
}

// Tell Firebase which language to use for its own emails (password reset,
// email verification) and reCAPTCHA, so they match the customer's UI language.
// Accepts our UI codes (e.g. "DA", "SE") and maps to the locale Firebase expects.
const FIREBASE_LOCALE: Record<string, string> = {
  DA: "da", EN: "en", DE: "de", ES: "es", FR: "fr",
  IT: "it", NL: "nl", PL: "pl", PT: "pt", SE: "sv",
};

export async function setAuthLanguage(code: string): Promise<void> {
  try {
    const auth = await getAuthInstance();
    const key = String(code || "").trim().toUpperCase();
    auth.languageCode = FIREBASE_LOCALE[key] || "en";
  } catch {
    /* non-fatal: language is a nicety, never block on it */
  }
}

// ── Auth helpers ────────────────────────────────────────────────────
// Try popup first (works without 3rd-party cookies); fall back to redirect
// when the popup is blocked or the browser refuses to open one. Caller-side
// awaiting this returns the credential when popup succeeds, or null when we
// fell back to redirect (in which case getGoogleRedirectResult handles it).
export async function loginWithGoogleRedirect(): Promise<UserCredential | null> {
  const [{ GoogleAuthProvider, signInWithPopup, signInWithRedirect }, auth] = await Promise.all([
    getFirebaseAuthModule(),
    getAuthInstance(),
  ]);
  const googleProvider = new GoogleAuthProvider();
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (err) {
    const code = (err as { code?: string })?.code || "";
    const popupBlocked = /popup-blocked|popup-closed-by-user|cancelled-popup-request|operation-not-supported-in-this-environment/i.test(code);
    if (popupBlocked) {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw err;
  }
}

export const getGoogleRedirectResult = async () => {
  const [{ getRedirectResult }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return getRedirectResult(auth);
};

export const loginWithEmail = async (email: string, password: string) => {
  const [{ signInWithEmailAndPassword }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return signInWithEmailAndPassword(auth, email, password);
};

export const signupWithEmail = async (email: string, password: string) => {
  const [{ createUserWithEmailAndPassword }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return createUserWithEmailAndPassword(auth, email, password);
};

export const resetPassword = async (email: string) => {
  const [{ sendPasswordResetEmail }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return sendPasswordResetEmail(auth, email);
};

export function userHasPasswordProvider(user: User | null): boolean {
  return Boolean(user?.providerData?.some(provider => provider.providerId === "password"));
}

export function userHasGoogleProvider(user: User | null): boolean {
  return Boolean(user?.providerData?.some(provider => provider.providerId === "google.com"));
}

export async function changeUserPassword(currentPassword: string, newPassword: string) {
  const [{ EmailAuthProvider, reauthenticateWithCredential, updatePassword }, auth] = await Promise.all([
    getFirebaseAuthModule(),
    getAuthInstance(),
  ]);
  const user = auth.currentUser;
  if (!user?.email) {
    const err = new Error("Not signed in with email") as Error & { code?: string };
    err.code = "auth/user-not-found";
    throw err;
  }
  if (String(newPassword || "").length < 8) {
    const err = new Error("Password should be at least 8 characters") as Error & { code?: string };
    err.code = "auth/weak-password";
    throw err;
  }
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

export const updateUserProfile = async (user: User, profile: { displayName?: string; photoURL?: string }) => {
  const { updateProfile } = await getFirebaseAuthModule();
  return updateProfile(user, profile);
};

export const sendLoginLink = async (email: string) => {
  const [{ sendSignInLinkToEmail }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return sendSignInLinkToEmail(auth, email, {
    url: `${window.location.origin}/login`,
    handleCodeInApp: true,
  });
};

export const isLoginLink = async (url: string) => {
  const [{ isSignInWithEmailLink }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return isSignInWithEmailLink(auth, url);
};

export const completeLoginWithLink = async (email: string, url: string) => {
  const [{ signInWithEmailLink }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return signInWithEmailLink(auth, email, url);
};

export const logout = async () => {
  const [{ signOut }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return signOut(auth);
};

export const onAuth = async (cb: (user: User | null) => void) => {
  const [{ onAuthStateChanged }, auth] = await Promise.all([getFirebaseAuthModule(), getAuthInstance()]);
  return onAuthStateChanged(auth, cb);
};
